import type { AnalysisChord, AnalysisVoicing } from "./analyzer-types";
import type { AnnotatedNote } from "./part";
import { ROOT_PITCH_CLASS, QUALITY_INTERVALS } from "./theory-helpers";

type Hand = "right" | "left";

type Inversion = AnalysisVoicing["inversion"];

const FINGER_PATTERNS: Record<number, Record<Hand, number[]>> = {
  1: { right: [3], left: [3] },
  2: { right: [1, 5], left: [5, 1] },
  3: { right: [1, 3, 5], left: [5, 3, 1] },
  4: { right: [1, 2, 3, 5], left: [5, 3, 2, 1] },
  5: { right: [1, 2, 3, 4, 5], left: [5, 4, 3, 2, 1] },
};

const INVERSION_ROT: Record<string, number> = {
  root: 0,
  "1st": 1,
  "2nd": 2,
  "3rd": 3,
};

const DEFAULT_ANCHOR: Record<Hand, number> = {
  right: 60,
  left: 48,
};

/**
 * Doigté académique pour un accord plaqué d'une main.
 * Les MIDI sont supposés triés du grave à l'aigu.
 */
export function academicChordFingering(count: number, hand: Hand): number[] {
  const pattern = FINGER_PATTERNS[count];
  if (pattern) {
    return pattern[hand];
  }

  const base = hand === "right" ? [1, 2, 3, 4, 5] : [5, 4, 3, 2, 1];

  return Array.from({ length: count }, (_, i) => base[Math.min(i, 4)]);
}

/**
 * Construit un voicing plaqué académique (close position) à partir du root,
 * de la qualité et du renversement. Les notes sont triées du grave à l'aigu,
 * la basse placée proche d'un point d'ancrage (par défaut C4 pour MD, C3 pour MG).
 */
export function academicChordVoicing(
  root: string,
  quality: string,
  inversion: Inversion,
  hand: Hand,
  anchorMidi?: number,
): number[] {
  const rootPC = ROOT_PITCH_CLASS[root];
  const intervals = QUALITY_INTERVALS[quality];
  if (rootPC === undefined || !intervals) {
    return [];
  }

  const rot = inversion ? (INVERSION_ROT[inversion] ?? 0) : 0;
  const pcs: number[] = [];
  for (let k = 0; k < intervals.length; k++) {
    const idx = (k + rot) % intervals.length;
    pcs.push((rootPC + intervals[idx]) % 12);
  }

  const anchor = anchorMidi ?? DEFAULT_ANCHOR[hand];
  const bass = nearestMidiWithPC(pcs[0], anchor);

  const midis: number[] = [bass];
  for (let k = 1; k < pcs.length; k++) {
    let m = midis[k - 1] + 1;
    while (m % 12 !== pcs[k]) {
      m++;
    }

    midis.push(m);
  }

  return midis;
}

// ── Didactic moment voicing ───────────────────────────────────

const FALLBACK_ANCHOR: Record<Hand, number> = { right: 60, left: 48 };

/** Per-hand voicing computed for a single moment in the score. */
export type MomentVoicing = {
  /** Concatenated midis (LH then RH), ready to feed Chord.fromMidi. */
  midis: number[];
  /** Per-midi hand colour, parallel to `midis`. */
  hands: Hand[];
  /** Per-midi academic finger, parallel to `midis`. */
  fingers: number[];
  /** Subset of `midis` assigned to the LH (used for hint colouring). */
  leftMidis: number[];
  /** Source notes used for the RH voicing (deduped + sorted). */
  rightNotes: AnnotatedNote[];
  /** Source notes used for the LH voicing (deduped + sorted). */
  leftNotes: AnnotatedNote[];
};

function dedupeSortedByMidi(notes: AnnotatedNote[]): AnnotatedNote[] {
  const seen = new Set<number>();
  const out: AnnotatedNote[] = [];

  for (const note of notes) {
    const midi = note.note.toMidi();

    if (!seen.has(midi)) {
      seen.add(midi);
      out.push(note);
    }
  }

  return out.sort((a, b) => a.note.toMidi() - b.note.toMidi());
}

/**
 * Builds the per-hand academic voicing for a chord moment, anchored on the
 * actually-played register. When a hand played notes that match the academic
 * voicing, we return the intersection; otherwise we fall back to the full
 * academic voicing so the chord card never appears empty when the score has
 * notes for that hand.
 */
function buildHandVoicing(
  chord: AnalysisChord,
  played: number[],
  hand: Hand,
): number[] {
  if (played.length === 0) {
    return [];
  }

  // A melodic hand never states the chord, so the inversion recorded next to
  // it describes a shape it did not play (`Em/F#` for a line passing through
  // an F#). Fall back to the chord-level inversion in that case.
  const handEntry = chord.handVoicing?.[hand];
  const handInversion =
    (handEntry?.role === "melodic" ? undefined : handEntry?.inversion) ??
    chord.voicing?.inversion ??
    "root";
  const anchor = played[0] ?? FALLBACK_ANCHOR[hand];
  const academic = academicChordVoicing(
    chord.root,
    chord.quality,
    handInversion,
    hand,
    anchor,
  );

  if (academic.length === 0) {
    return [];
  }

  const academicSet = new Set(academic);
  const intersection = played.filter((midi) => academicSet.has(midi));

  return intersection.length > 0 ? intersection : academic;
}

/**
 * Builds the didactic voicing for a single chord moment from the actually
 * played notes. Returns `null` when neither hand has any voicing to display.
 */
export function buildMomentVoicing(
  chord: AnalysisChord,
  chordNotes: AnnotatedNote[],
): MomentVoicing | null {
  const rightNotes = dedupeSortedByMidi(
    chordNotes.filter((n) => n.hand === "right"),
  );
  const leftNotes = dedupeSortedByMidi(
    chordNotes.filter((n) => n.hand === "left"),
  );

  const rightMidisPlayed = rightNotes.map((n) => n.note.toMidi());
  const leftMidisPlayed = leftNotes.map((n) => n.note.toMidi());

  const rhVoicing = buildHandVoicing(chord, rightMidisPlayed, "right");
  const lhVoicing = buildHandVoicing(chord, leftMidisPlayed, "left");

  if (rhVoicing.length === 0 && lhVoicing.length === 0) {
    return null;
  }

  const midis = [...lhVoicing, ...rhVoicing];
  const hands: Hand[] = [
    ...lhVoicing.map(() => "left" as const),
    ...rhVoicing.map(() => "right" as const),
  ];
  const fingers = [
    ...academicChordFingering(lhVoicing.length, "left"),
    ...academicChordFingering(rhVoicing.length, "right"),
  ];

  return {
    midis,
    hands,
    fingers,
    leftMidis: lhVoicing,
    rightNotes,
    leftNotes,
  };
}

// ── Helpers ────────────────────────────────────────────────────

function nearestMidiWithPC(pc: number, reference: number): number {
  const refPC = ((reference % 12) + 12) % 12;
  let delta = (((pc - refPC) % 12) + 12) % 12;
  if (delta > 6) {
    delta -= 12;
  }

  return reference + delta;
}
